import type { ConstraintLadderPolicyProps } from './types'

import { MaturityOption } from '@moment/horizon-data-access/proposal'
import { useCallback, useMemo, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import { ButtonSize } from '@/components/atomic/button/variants'
import { ButtonMultiSelect } from '@/components/atomic/button-multi-select'
import Input from '@/components/atomic/input'
import { Select } from '@/components/atomic/select'
import Slider from '@/components/atomic/slider'
import {
  type BarConfig,
  TargetBarChart,
} from '@/components/atomic/target-bar-chart'
import { ModelBuilderPlaceholder } from '@/components/molecules/model-builder-placeholder'
import ModelConstraintCard from '@/components/molecules/model-constraint-card'
import TargetBandInput, {
  TargetBandInputType,
  TargetBandSlot,
} from '@/components/molecules/target-band/styles'
import { BucketType } from '@/components/pages/model-builder/default-values'
import { sliderConfigs } from '@/components/pages/model-builder/model-setup-tab'

import { calculateBucketValues } from './helpers'
import {
  ModelBucketRowContainer,
  ModelBucketWeightHeader,
  ModelBucketWeightLegend,
  ModelBucketWidthCustomContainer,
  ModelBucketWidthInputContainer,
  ModelConstraintBucketWeightContainer,
  ModelConstraintBucketWeightInputContainer,
  ModelMaturitySlider,
} from './styles'

const useChartData = (
  numberOfBuckets: number,
  defaultWeight: number,
  rangeInPeriods: number[] | null,
  bucketType: BucketType = BucketType.Years,
  bucketWidth: number = 12,
) => {
  return useMemo(() => {
    if (!numberOfBuckets) return []

    const [startPeriod] = rangeInPeriods || [0]
    return Array.from({ length: numberOfBuckets }, (_, i) => {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      const periodNumber = startPeriod! + i
      const startRange = periodNumber * bucketWidth
      const endRange = (periodNumber + 1) * bucketWidth
      return {
        [bucketType === BucketType.Months ? 'Month' : 'Year']:
          bucketType === BucketType.Months
            ? `${startRange}-${endRange}`
            : `${startRange / 12}-${endRange / 12}`,
        value: defaultWeight,
        target: defaultWeight,
      }
    })
  }, [numberOfBuckets, defaultWeight, rangeInPeriods, bucketType, bucketWidth])
}

const BUCKET_WIDTH_OPTIONS = [
  { label: '3 months', value: '3' },
  { label: '6 months', value: '6' },
  { label: '1 year', value: '12' },
]

const BUCKET_TYPE_OPTIONS = [
  { label: 'Months', value: BucketType.Months },
  { label: 'Years', value: BucketType.Years },
]

const MATURITY_OPTIONS = [
  { label: 'Maturity date', value: MaturityOption.maturity_date },
  { label: 'Call-adjusted date', value: MaturityOption.workout_date },
  { label: 'Next call date', value: MaturityOption.next_call_date },
]

const mockBarConfig: BarConfig[] = [
  {
    key: 'value',
    fill: '#5A7B7D',
    radius: [4, 4, 0, 0],
  },
]

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ConstraintLadderPolicy({
  data,
  onChange,
  disabled = false,
}: ConstraintLadderPolicyProps) {
  const [bucketType, setBucketType] = useState<BucketType>(() => {
    return data.period_length % 12 === 0 ? BucketType.Years : BucketType.Months
  })

  const calculatedBucketType = useMemo(() => {
    return data.period_length % 12 === 0 ? BucketType.Years : BucketType.Months
  }, [data.period_length])

  if (bucketType !== calculatedBucketType) {
    setBucketType(calculatedBucketType)
  }

  const isStandardOption = useMemo(() => {
    return [3, 6, 12].includes(data.period_length)
  }, [data.period_length])

  const [isCustomWidth, setIsCustomWidth] = useState(false)
  const [customWidthValue, setCustomWidthValue] = useState('')

  const customWidthDisplayValue = useMemo(() => {
    if (isStandardOption) return ''
    return bucketType === BucketType.Years
      ? (data.period_length / 12).toString()
      : data.period_length.toString()
  }, [data.period_length, bucketType, isStandardOption])

  const shouldShowCustomWidth = useMemo(() => {
    return !isStandardOption || isCustomWidth
  }, [isStandardOption, isCustomWidth])

  const rangeInPeriods = useMemo(() => {
    return [data.start_period, data.end_period]
  }, [data.start_period, data.end_period])

  const useBucketCalculations = (rangeInPeriods: number[] | null) => {
    return useMemo(
      () => calculateBucketValues(rangeInPeriods || [0, 0]),
      [rangeInPeriods],
    )
  }

  const validateAndAdjustRange = useCallback(
    (newBucketWidth: number) => {
      if (rangeInPeriods.length >= 2) {
        const endPeriod = rangeInPeriods[1]
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        if (newBucketWidth * endPeriod! > 720) {
          const maxEndPeriod = Math.floor(720 / newBucketWidth)
          const adjustedRange = [rangeInPeriods[0], maxEndPeriod]
          onChange({
            ...data,
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            start_period: adjustedRange[0]!,
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            end_period: adjustedRange[1]!,
            period_length: newBucketWidth,
          })
        } else {
          onChange({ ...data, period_length: newBucketWidth })
        }
      } else {
        onChange({ ...data, period_length: newBucketWidth })
      }
    },
    [rangeInPeriods, data, onChange],
  )

  const setDefaultCustomWidth = useCallback(() => {
    const defaultCustomWidth = '2'
    setCustomWidthValue(defaultCustomWidth)
    const numValue = parseInt(defaultCustomWidth, 10)
    const actualBucketWidth =
      bucketType === BucketType.Years ? numValue * 12 : numValue
    validateAndAdjustRange(actualBucketWidth)
  }, [bucketType, validateAndAdjustRange])

  const bucketTypeLabel = useMemo(
    () => (bucketType === BucketType.Months ? 'months' : 'years'),
    [bucketType],
  )

  const xAxisLabel = useMemo(() => `${bucketTypeLabel}`, [bucketTypeLabel])

  const handleBucketTypeChange = useCallback(
    (value: string) => {
      setBucketType(value as BucketType)
      if (data.period_length && customWidthValue) {
        const numValue = parseInt(customWidthValue, 10)
        if (numValue > 0) {
          const actualBucketWidth =
            value === BucketType.Years ? numValue * 12 : numValue
          validateAndAdjustRange(actualBucketWidth)
        }
      }
    },
    [data.period_length, customWidthValue, validateAndAdjustRange],
  )

  const handleBucketWidthChange = useCallback(
    (value: string[]) => {
      setIsCustomWidth(false)
      setCustomWidthValue('')
      if (value.length === 0 || !value[0]) {
        return
      }
      const parsedValue = parseInt(value[0], 10)
      validateAndAdjustRange(parsedValue)

      if (parsedValue % 12 !== 0) {
        setBucketType(BucketType.Months)
      } else {
        setBucketType(BucketType.Years)
      }
    },
    [validateAndAdjustRange],
  )

  const handleCustomWidthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (value === '' || (/^\d+$/.test(value) && parseInt(value, 10) > 0)) {
        setCustomWidthValue(value)
        if (value !== '') {
          const numValue = parseInt(value, 10)
          if (numValue > 0) {
            const actualBucketWidth =
              bucketType === BucketType.Years ? numValue * 12 : numValue
            validateAndAdjustRange(actualBucketWidth)
          }
        }
      }
    },
    [bucketType, validateAndAdjustRange],
  )

  const periodToRangeConverter = useCallback(
    (periods: number) => {
      if (bucketType === BucketType.Years) {
        return Math.min(60, (periods * data.period_length) / 12)
      }
      return Math.min(720, periods * data.period_length)
    },
    [bucketType, data.period_length],
  )

  const rangeToPeriodConverter = useCallback(
    (range: number[]) => {
      const width = data.period_length || 12
      if (bucketType === BucketType.Years) {
        return range.map((r) => (r * 12) / width)
      }
      return range.map((r) => r / width)
    },
    [bucketType, data.period_length],
  )

  const handleRangeChange = useCallback(
    (value: number[]) => {
      requestAnimationFrame(() => {
        const width = data.period_length || 12
        const roundedValues = value.map((val) => {
          if (bucketType === BucketType.Years) {
            const bucketSizeInYears = width / 12
            return Math.min(
              60,
              Math.floor(val / bucketSizeInYears) * bucketSizeInYears,
            )
          } else {
            return Math.min(720, Math.floor(val / width) * width)
          }
        })

        const convertedValue = rangeToPeriodConverter(roundedValues)
        onChange({
          ...data,
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          start_period: convertedValue[0]!,
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          end_period: convertedValue[1]!,
        })
      })
    },
    [data, bucketType, rangeToPeriodConverter, onChange],
  )

  const sliderDisplayValue = useMemo(() => {
    if (!rangeInPeriods || !data.period_length) {
      return sliderConfigs.rangeInPeriods.defaultValue.map((p) =>
        periodToRangeConverter(p),
      )
    }
    return rangeInPeriods.map((period) => periodToRangeConverter(period))
  }, [rangeInPeriods, data.period_length, periodToRangeConverter])

  const sliderFormatter = useCallback(
    (value: number) => {
      const width = data.period_length || 12

      if (bucketType === BucketType.Years) {
        const bucketSizeInYears = width / 12
        const roundedValue =
          Math.floor(value / bucketSizeInYears) * bucketSizeInYears
        return roundedValue.toString()
      } else {
        const roundedValue = Math.floor(value / width) * width
        return roundedValue.toString()
      }
    },
    [bucketType, data.period_length],
  )

  const onWeightToleranceChange = useCallback(
    (value: string) => {
      const parsedValue = value === '' ? '0' : value
      const weightTolerance = parseFloat(parsedValue) / 100
      onChange(
        {
          ...data,
          weight_tolerance: weightTolerance,
        },
        { weightToleranceEdited: true },
      )
    },
    [data, onChange],
  )

  const weightToleranceDisplayValue = useMemo(() => {
    return data.weight_tolerance ? (data.weight_tolerance * 100).toString() : ''
  }, [data.weight_tolerance])

  const onMaturityOptionChange = useCallback(
    (values: string[]) => {
      const value = values[0] as MaturityOption | undefined
      if (value) {
        onChange({
          ...data,
          maturity_option: value,
        })
      }
    },
    [data, onChange],
  )

  const currentMaturityOption = useMemo(() => {
    if (data.maturity_option) {
      return data.maturity_option
    }
    if (data.use_call_adjusted_maturity === true) {
      return MaturityOption.workout_date
    }
    return MaturityOption.maturity_date
  }, [data.maturity_option, data.use_call_adjusted_maturity])

  const { numberOfBuckets, defaultWeight, defaultMin, defaultMax } =
    useBucketCalculations(rangeInPeriods)

  const chartData = useChartData(
    numberOfBuckets,
    defaultWeight,
    rangeInPeriods,
    bucketType,
    data.period_length,
  )

  const bucketMin = useMemo(() => {
    const numValue = data.weight_tolerance * 100
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    if (isNaN(numValue)) return defaultMin
    const min = Math.max(0, defaultWeight - numValue)
    return min
  }, [data.weight_tolerance, defaultMin, defaultWeight])

  const bucketMax = useMemo(() => {
    const numValue = data.weight_tolerance * 100
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    if (isNaN(numValue)) return defaultMax
    const max = defaultWeight + numValue
    return max
  }, [data.weight_tolerance, defaultMax, defaultWeight])

  return (
    <div className="w-full flex flex-col gap-4">
      <ModelConstraintCard
        title="Maturity date option"
        className="bg-background"
        tooltipText="Choose which date to use for ladder policy calculations"
      >
        <ButtonMultiSelect
          className="bg-background"
          columns={3}
          isMultiSelect={false}
          options={MATURITY_OPTIONS}
          value={[currentMaturityOption]}
          onValueChange={onMaturityOptionChange}
          disabled={disabled}
          minSelected={1}
        />
      </ModelConstraintCard>
      <ModelBucketRowContainer>
        <ModelConstraintCard title="Bucket width" className="bg-background">
          <ButtonMultiSelect
            className="bg-background"
            columns={3}
            isMultiSelect={false}
            options={BUCKET_WIDTH_OPTIONS}
            value={
              shouldShowCustomWidth
                ? []
                : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                  data.period_length
                  ? [data.period_length.toString()]
                  : []
            }
            onValueChange={handleBucketWidthChange}
            disabled={disabled}
          />
          <ModelBucketWidthCustomContainer>
            {!shouldShowCustomWidth ? (
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  setIsCustomWidth(true)
                  setDefaultCustomWidth()
                }}
                label="Custom width"
                size={ButtonSize.Large}
                className="w-full"
                disabled={disabled}
              />
            ) : (
              <ModelBucketWidthInputContainer>
                <Input
                  type="number"
                  size="large"
                  placeholder="Enter"
                  value={customWidthDisplayValue || customWidthValue}
                  onChange={handleCustomWidthChange}
                  disabled={disabled}
                />
                <Select
                  options={BUCKET_TYPE_OPTIONS}
                  value={bucketType}
                  onValueChange={handleBucketTypeChange}
                  hideSearch={true}
                  disabled={disabled}
                />
              </ModelBucketWidthInputContainer>
            )}
          </ModelBucketWidthCustomContainer>
        </ModelConstraintCard>
        <ModelConstraintCard
          title={`Maturity range in ${bucketType === BucketType.Months ? 'months' : 'years'}`}
          tooltipText="Bucket width tooltip text"
          className="bg-background"
        >
          <ModelMaturitySlider>
            <Slider
              key={`${data.period_length}-${bucketType}-${data.start_period}-${data.end_period}`}
              defaultValue={sliderDisplayValue}
              min={0}
              max={bucketType === BucketType.Years ? 60 : 720}
              step={periodToRangeConverter(sliderConfigs.rangeInPeriods.step)}
              formatter={sliderFormatter}
              hidePlus
              showMinMaxInputs
              onValueChange={handleRangeChange}
              disabled={disabled}
              className="mt-[10px]"
            />
          </ModelMaturitySlider>
        </ModelConstraintCard>
      </ModelBucketRowContainer>

      <ModelConstraintCard
        title="Bucket weight tolerance"
        subtitle={
          numberOfBuckets > 0 && (
            <ModelBucketWeightLegend text="Shaded area is the weight tolerance" />
          )
        }
        tooltipText="Buckets are permitted to deviate from their target weight by up to the bucket weight tolerance."
        rightHeaderContent={
          numberOfBuckets > 0 ? (
            <ModelBucketWeightHeader>
              <ModelConstraintBucketWeightInputContainer>
                <TargetBandInput
                  type={TargetBandInputType.Text}
                  slot={TargetBandSlot.Buffer}
                  placeholder="Enter"
                  value={weightToleranceDisplayValue}
                  onChange={onWeightToleranceChange}
                  disabled={disabled}
                />
              </ModelConstraintBucketWeightInputContainer>
            </ModelBucketWeightHeader>
          ) : null
        }
      >
        <ModelConstraintBucketWeightContainer>
          {numberOfBuckets > 0 ? (
            <TargetBarChart
              data={chartData}
              xAxisKey={bucketType === BucketType.Months ? 'Month' : 'Year'}
              bars={mockBarConfig}
              minValue={bucketMin}
              maxValue={bucketMax}
              targetValue={defaultWeight}
              xAxisLabel={xAxisLabel}
              tooltipLabelPrefix={
                bucketType === BucketType.Months ? 'Months' : 'Years'
              }
            />
          ) : (
            <ModelBuilderPlaceholder text="Configure the maturity range to see adjustable bucket weights" />
          )}
        </ModelConstraintBucketWeightContainer>
      </ModelConstraintCard>
    </div>
  )
}
